import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Inbox, Mail, Building2, MessageSquare, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin-only listing for Elementary Pilot leads captured via
 * POST /api/leads/elementary-pilot. Intentionally tiny: one page, sortable
 * only by created_at desc (server-side), copy-email shortcut, no CRM.
 */
export default function AdminLeadsPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/leads/elementary-pilot`);
      setLeads(r.data?.leads || []);
    } catch (e) {
      setError(
        e?.response?.status === 403
          ? "Admin access required."
          : "Couldn't load leads — please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch {
      return iso;
    }
  };

  const copyEmail = (email) => {
    if (!email) return;
    navigator.clipboard?.writeText(email).catch(() => {});
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm"
            data-testid="admin-leads-back"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to admin
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50"
            data-testid="admin-leads-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-10">
        <header className="mb-8">
          <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-xs font-semibold mb-3">
            <Inbox className="w-3.5 h-3.5" />
            Elementary Pilot Inbox
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
            Lemonade Leaders Jr. — Pilot Inquiries
          </h1>
          <p className="mt-2 text-gray-600">
            Inbound pilot interest from schools, funders, and mentors. Most
            recent first.
          </p>
        </header>

        {error && (
          <div className="mb-6 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <section className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700">
              {loading ? "Loading…" : `${leads.length} lead${leads.length === 1 ? "" : "s"}`}
            </span>
          </div>

          {!loading && leads.length === 0 && !error ? (
            <div className="px-5 py-16 text-center text-gray-500" data-testid="admin-leads-empty">
              <Inbox className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="font-medium text-gray-700 mb-1">No leads yet</p>
              <p className="text-sm">
                When someone submits the pilot form, they'll appear here.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {leads.map((l) => (
                <article
                  key={l.id}
                  className="px-5 py-5 hover:bg-gray-50/60 transition-colors"
                  data-testid={`admin-lead-${l.id}`}
                >
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-1">
                    <h3 className="text-lg font-bold text-gray-900">{l.name}</h3>
                    <span className="text-xs text-gray-500">
                      {fmtDate(l.created_at)}
                    </span>
                    <span className="ml-auto text-xs font-semibold text-yellow-700 bg-yellow-50 px-2 py-0.5 rounded-full uppercase tracking-wide">
                      {l.source}
                    </span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => copyEmail(l.email)}
                      className="flex items-center gap-2 text-sm text-gray-800 hover:text-gray-900 bg-gray-50 rounded-lg px-3 py-2 text-left truncate"
                      title="Click to copy"
                    >
                      <Mail className="w-4 h-4 text-gray-500 shrink-0" />
                      <span className="truncate">{l.email}</span>
                    </button>
                    <div className="flex items-center gap-2 text-sm text-gray-800 bg-gray-50 rounded-lg px-3 py-2 truncate">
                      <Building2 className="w-4 h-4 text-gray-500 shrink-0" />
                      <span className="truncate">
                        {l.organization || <span className="text-gray-400 italic">No organization</span>}
                      </span>
                    </div>
                  </div>
                  {l.message && (
                    <div className="mt-3 flex items-start gap-2 text-sm text-gray-700 bg-yellow-50/60 rounded-lg px-3 py-3 ring-1 ring-yellow-100">
                      <MessageSquare className="w-4 h-4 text-yellow-600 shrink-0 mt-0.5" />
                      <p className="leading-relaxed whitespace-pre-wrap">{l.message}</p>
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
